import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement | undefined} stickyAddToCartBar - Sticky mobile/tablet add to cart bar.
 * @property {HTMLElement | undefined} stickyQuantitySelector - Sticky quantity selector container.
 * @property {HTMLButtonElement | undefined} stickyQuantityMinus - Sticky quantity decrease button.
 * @property {HTMLButtonElement | undefined} stickyQuantityPlus - Sticky quantity increase button.
 * @property {HTMLElement | undefined} stickyQuantityValue - Sticky quantity value.
 * @property {HTMLButtonElement | undefined} stickyAddToCartButton - Sticky mobile/tablet add to cart button.
 * @property {HTMLElement | undefined} stickyAddToCartButtonLabel - Sticky button label.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();
  #stickyAddToCartObserver;
  #stickyAddToCartResizeObserver;
  #isMainBuyButtonsVisible = true;

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#syncStickyQuantityState, { signal });
    this.refs.stickyQuantityMinus?.addEventListener('click', this.#handleStickyQuantityDecrease, { signal });
    this.refs.stickyQuantityPlus?.addEventListener('click', this.#handleStickyQuantityIncrease, { signal });
    this.refs.stickyAddToCartButton?.addEventListener('click', this.#handleStickyAddToCartClick, { signal });
    window.addEventListener('resize', this.#syncStickyAddToCartVisibility, { signal });
    this.#setupStickyAddToCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#stickyAddToCartObserver?.disconnect();
    this.#stickyAddToCartResizeObserver?.disconnect();
    this.#setStickyAddToCartOffset(0);
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    const currentAddToCartButton = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    const resolvedVariantId = this.refs.variantId.value || this.#getSelectedVariantId();

    // Check if the add to cart button is disabled and do an early return if it is
    if (currentAddToCartButton?.disabled) return;
    if (!resolvedVariantId) return;

    this.refs.variantId.value = resolvedVariantId;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const { quantityInput } = this.#getMainQuantityRefs();
    if (quantityInput instanceof HTMLInputElement) {
      const min = quantityInput.min === '' ? null : Number(quantityInput.min);
      const max = quantityInput.max === '' ? null : Number(quantityInput.max);
      const fallbackQuantity = Number(this.dataset.quantityDefault || '1');
      let nextQuantity = Number.parseInt(quantityInput.value, 10);

      if (Number.isNaN(nextQuantity)) nextQuantity = fallbackQuantity;
      if (min !== null && !Number.isNaN(min)) nextQuantity = Math.max(nextQuantity, min);
      if (max !== null && !Number.isNaN(max)) nextQuantity = Math.min(nextQuantity, max);

      quantityInput.value = `${nextQuantity}`;
      this.#syncStickyQuantityState();
    }

    const formData = new FormData(form);
    const cartItems = this.#buildCartItems(formData);
    const requestedItemCount = cartItems.reduce((total, item) => total + item.quantity, 0);
    const sections = this.#getCartSectionIds();
    const hasExtraProduct = cartItems.length > 1;
    const fetchCfg = hasExtraProduct
      ? {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: cartItems,
            ...(sections ? { sections } : {}),
          }),
        }
      : fetchConfig('javascript', {
          body: this.#buildSingleItemFormData(formData, sections),
        });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          const errorMessage =
            typeof response.description === 'string' && response.description.trim()
              ? response.description
              : typeof response.message === 'string' && response.message.trim()
                ? response.message
                : 'Cart Error';

          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(errorMessage);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: requestedItemCount,
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (currentAddToCartButton) {
            const addToCartButton = currentAddToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: requestedItemCount,
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  #buildSingleItemFormData(formData, sections) {
    const requestFormData = new FormData();

    for (const [key, value] of formData.entries()) {
      requestFormData.append(key, value);
    }

    if (sections) {
      requestFormData.append('sections', sections);
    }

    return requestFormData;
  }

  #buildCartItems(formData) {
    const primaryItem = this.#buildPrimaryCartItem(formData);
    const selectedExtraItem = this.#getSelectedExtraItem();

    if (selectedExtraItem) {
      return [primaryItem, selectedExtraItem];
    }

    return [primaryItem];
  }

  #buildPrimaryCartItem(formData) {
    const quantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
    const id = Number(formData.get('id'));
    const primaryItem = {
      id,
      quantity,
    };
    const properties = {};

    for (const [key, value] of formData.entries()) {
      if (typeof value !== 'string' || value === '') continue;

      if (key === 'selling_plan') {
        primaryItem.selling_plan = value;
        continue;
      }

      if (key.startsWith('properties[') && key.endsWith(']')) {
        const propertyName = key.slice(11, -1);
        properties[propertyName] = value;
      }
    }

    if (Object.keys(properties).length > 0) {
      primaryItem.properties = properties;
    }

    return primaryItem;
  }

  #getSelectedExtraItem() {
    const extraCheckbox = this.closest('.product-information')?.querySelector('.product-extra__checkbox:checked');

    if (!(extraCheckbox instanceof HTMLInputElement)) return null;

    const variantId = Number(extraCheckbox.dataset.extraVariantId);
    if (!variantId) return null;

    return {
      id: variantId,
      quantity: 1,
    };
  }

  #getCartSectionIds() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });

    return cartItemComponentsSectionIds.join(',');
  }

  #setupStickyAddToCart() {
    const { stickyAddToCartBar } = this.refs;
    const productFormButtons = this.querySelector('.product-form-buttons');

    if (!stickyAddToCartBar || !productFormButtons) return;

    this.#stickyAddToCartObserver?.disconnect();
    this.#stickyAddToCartObserver = new IntersectionObserver(
      ([entry]) => {
        this.#isMainBuyButtonsVisible = Boolean(entry?.isIntersecting);
        this.#syncStickyAddToCartVisibility();
      },
      {
        threshold: 0.2,
      }
    );

    this.#stickyAddToCartObserver.observe(productFormButtons);
    this.#stickyAddToCartResizeObserver?.disconnect();
    this.#stickyAddToCartResizeObserver = new ResizeObserver(() => {
      this.#syncStickyAddToCartOffset();
    });
    this.#stickyAddToCartResizeObserver.observe(stickyAddToCartBar);
    this.#syncStickyQuantityState();
    this.#syncStickyAddToCartButton();
    this.#syncStickyAddToCartOffset();
    this.#syncStickyAddToCartVisibility();
  }

  #syncStickyAddToCartVisibility = () => {
    const { stickyAddToCartBar } = this.refs;
    if (!stickyAddToCartBar) return;

    const isCompactViewport = window.matchMedia('(max-width: 1024px)').matches;
    const isVisible = isCompactViewport && !this.#isMainBuyButtonsVisible;

    stickyAddToCartBar.dataset.stickyState = isVisible ? 'visible' : 'hidden';
    stickyAddToCartBar.setAttribute('aria-hidden', String(!isVisible));
    this.#syncStickyAddToCartOffset();
  };

  #syncStickyAddToCartOffset() {
    const { stickyAddToCartBar } = this.refs;
    if (!stickyAddToCartBar) return;

    const isCompactViewport = window.matchMedia('(max-width: 1024px)').matches;
    if (!isCompactViewport) {
      this.#setStickyAddToCartOffset(0);
      return;
    }

    this.#setStickyAddToCartOffset(stickyAddToCartBar.offsetHeight);
  }

  #setStickyAddToCartOffset(offset) {
    const productInformation = this.closest('.product-information');
    if (!(productInformation instanceof HTMLElement)) return;

    if (!offset) {
      productInformation.style.removeProperty('--pdp-sticky-add-to-cart-offset');
      return;
    }

    productInformation.style.setProperty('--pdp-sticky-add-to-cart-offset', `${offset}px`);
  }

  #getMainQuantityRefs() {
    const quantitySelector = this.querySelector('.product-form-buttons [ref="quantitySelector"]');
    if (!(quantitySelector instanceof HTMLElement)) return {};

    const quantityInput = quantitySelector.querySelector('[ref="quantityInput"]');
    const minusButton = quantitySelector.querySelector('[ref="minusButton"]');
    const plusButton = quantitySelector.querySelector('[ref="plusButton"]');

    return {
      quantityInput: quantityInput instanceof HTMLInputElement ? quantityInput : undefined,
      minusButton: minusButton instanceof HTMLButtonElement ? minusButton : undefined,
      plusButton: plusButton instanceof HTMLButtonElement ? plusButton : undefined,
    };
  }

  #getSelectedVariantId(variantPicker = this.#getVariantPicker()) {
    if (!(variantPicker instanceof HTMLElement)) return '';

    const picker = /** @type {{ selectedOption?: HTMLInputElement | HTMLOptionElement }} */ (variantPicker);
    const { selectedOption } = picker;

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return '';
    }

    return selectedOption.dataset.variantId ?? '';
  }

  #getVariantPicker() {
    const target = this.closest('.shopify-section, dialog, product-card');
    const variantPicker = target?.querySelector('variant-picker, swatches-variant-picker-component');

    return variantPicker instanceof HTMLElement ? variantPicker : null;
  }

  #syncStickyQuantityState = () => {
    const { stickyQuantitySelector, stickyQuantityMinus, stickyQuantityPlus, stickyQuantityValue } = this.refs;
    if (!stickyQuantitySelector || !stickyQuantityMinus || !stickyQuantityPlus || !stickyQuantityValue) return;

    const { quantityInput, minusButton, plusButton } = this.#getMainQuantityRefs();
    const fallbackQuantity = this.dataset.quantityDefault || '1';

    stickyQuantityValue.textContent = quantityInput?.value || fallbackQuantity;

    const quantityDisabled = quantityInput?.disabled ?? true;
    stickyQuantitySelector.setAttribute('aria-disabled', String(quantityDisabled));
    stickyQuantityMinus.disabled = quantityDisabled || Boolean(minusButton?.disabled);
    stickyQuantityPlus.disabled = quantityDisabled || Boolean(plusButton?.disabled);
  };

  #handleStickyQuantityDecrease = (event) => {
    event.preventDefault();

    const { minusButton } = this.#getMainQuantityRefs();
    if (!(minusButton instanceof HTMLButtonElement) || minusButton.disabled) return;

    minusButton.click();
  };

  #handleStickyQuantityIncrease = (event) => {
    event.preventDefault();

    const { plusButton } = this.#getMainQuantityRefs();
    if (!(plusButton instanceof HTMLButtonElement) || plusButton.disabled) return;

    plusButton.click();
  };

  #syncStickyAddToCartButton() {
    const { addToCartButtonContainer, stickyAddToCartButton, stickyAddToCartButtonLabel } = this.refs;
    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;

    if (!currentAddToCartButton || !stickyAddToCartButton || !stickyAddToCartButtonLabel) return;

    stickyAddToCartButton.disabled = currentAddToCartButton.disabled;
    stickyAddToCartButton.setAttribute('aria-disabled', String(currentAddToCartButton.disabled));

    const label =
      currentAddToCartButton.querySelector('.add-to-cart-text__content')?.textContent?.trim() ||
      currentAddToCartButton.textContent?.trim() ||
      '';

    if (label) {
      stickyAddToCartButtonLabel.textContent = label;
      stickyAddToCartButton.setAttribute('aria-label', label);
    }
  }

  #handleStickyAddToCartClick = (event) => {
    event.preventDefault();

    const currentAddToCartButton = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    if (!(currentAddToCartButton instanceof HTMLButtonElement) || currentAddToCartButton.disabled) return;

    currentAddToCartButton.click();
  };

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    this.#syncStickyAddToCartButton();
    this.#syncStickyQuantityState();
    this.#syncStickyAddToCartOffset();
    this.#syncStickyAddToCartVisibility();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = (event) => {
    this.refs.addToCartButtonContainer?.disable();
    const selectedVariantId = this.#getSelectedVariantId(event.target instanceof HTMLElement ? event.target : undefined);
    this.refs.variantId.value = selectedVariantId;
    if (this.refs.stickyAddToCartButton) {
      this.refs.stickyAddToCartButton.disabled = true;
      this.refs.stickyAddToCartButton.setAttribute('aria-disabled', 'true');
    }

    this.#syncStickyQuantityState();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
